import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import EnhancedResultsPanel from '../strategy/EnhancedResultsPanel.tsx';
import OptimizationResultsPanel from '../strategy/OptimizationResultsPanel.tsx';
import { BarChart, Bar, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, LineChart, Line, Legend, Customized } from 'recharts';
import { fetchShareLink } from '../../lib/api/shareLinks';
import { normalizeBacktestResult } from '../../lib/backtests/normalizeBacktestResult';

const formatNumber = (value, decimals = 2) => {
  if (!Number.isFinite(value)) return 'N/A';
  return value.toFixed(decimals);
};

const formatPrice = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return `$${value.toFixed(2)}`;
};

const ChartWatermark = ({ width = 0, height = 0 }) => (
  <text
    x={Math.max(width - 8, 0)}
    y={Math.max(height - 8, 0)}
    textAnchor="end"
    fontSize="11"
    fill="rgba(15, 23, 42, 0.25)"
  >
    factorbench.com
  </text>
);

const formatPercent = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return `${value.toFixed(2)}%`;
};

const formatReturnPercent = (value) => {
  if (!Number.isFinite(value)) return 'N/A';
  return `${(value * 100).toFixed(2)}%`;
};

const parseNumeric = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getFactorLabel = (factorId, strategyConfig, formulasOverride) => {
  if (!factorId) return 'Factor';
  if (factorId.startsWith('formula:')) {
    const formulaId = factorId.split(':')[1];
    const formulas = formulasOverride || strategyConfig?.formulas || [];
    const formula = formulas.find((item) => String(item.id) === String(formulaId));
    return formula?.name || `Formula ${formulaId}`;
  }
  return factorId.replace(/_/g, ' ');
};

const SharedScreenResults = ({ record }) => {
  const data = record?.screen_results || {};
  const elements = Array.isArray(data.elements) ? data.elements : [];
  const results = Array.isArray(data.results) ? data.results : [];
  const selected = results.filter((row) => row.selected);
  const factorIds = results[0]?.factors_displayed || [];
  const factorLabels = factorIds.map((factorId) => getFactorLabel(factorId, record?.strategy_config));

  const resolveElementFormulas = (elementId, index) => {
    const portfolioElements = record?.strategy_config?.portfolio?.elements;
    if (Array.isArray(portfolioElements) && portfolioElements.length > 0) {
      const matched = portfolioElements.find((el) => String(el.id) === String(elementId)) || portfolioElements[index];
      return matched?.formulas || [];
    }
    return record?.strategy_config?.formulas || [];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Screen Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <div>
              <div className="text-gray-500">Screen Date</div>
              <div className="font-medium text-gray-900">
                {data.screen_date || elements[0]?.screen_date || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Selected</div>
              <div className="font-medium text-emerald-600">
                {elements.length > 0
                  ? elements.reduce((acc, el) => acc + (el.selected_count || 0), 0)
                  : (data.selected_count ?? 'N/A')}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Total Universe</div>
              <div className="font-medium text-gray-900">
                {elements.length > 0
                  ? elements.reduce((acc, el) => acc + (el.total_universe || 0), 0)
                  : (data.total_universe ?? 'N/A')}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Weighting</div>
              <div className="font-medium text-gray-900">
                {(data.weighting_method_effective || data.weighting_method || 'N/A').replace(/_/g, ' ')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {elements.length > 0 ? (
        elements.map((element, index) => {
          const elementResults = Array.isArray(element.results) ? element.results : [];
          const elementSelected = elementResults.filter((row) => row.selected);
          const elementFormulas = resolveElementFormulas(element.id, index);
          const elementFactorIds = elementResults[0]?.factors_displayed || [];
          const elementFactorLabels = elementFactorIds.map((factorId) =>
            getFactorLabel(factorId, record?.strategy_config, elementFormulas)
          );
          const elementName = element.name || `Element ${index + 1}`;
          return (
            <Card key={element.id || elementName}>
              <CardHeader>
                <CardTitle className="text-lg">{elementName} ({elementSelected.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {elementSelected.length === 0 ? (
                  <div className="text-sm text-gray-500">No selected assets found.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border-collapse border border-gray-200">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="border border-gray-200 px-3 py-2">Ticker</th>
                          <th className="border border-gray-200 px-3 py-2">Price</th>
                          {elementFactorLabels.map((label, idx) => (
                            <th key={label + idx} className="border border-gray-200 px-3 py-2">
                              {label}
                            </th>
                          ))}
                          <th className="border border-gray-200 px-3 py-2">Weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        {elementSelected.map((row, idx) => (
                          <tr key={row.ticker || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="border border-gray-200 px-3 py-2 font-medium">{row.ticker}</td>
                            <td className="border border-gray-200 px-3 py-2">
                              {parseNumeric(row.price) !== null ? `$${formatNumber(parseNumeric(row.price), 2)}` : 'N/A'}
                            </td>
                            {elementFactorIds.map((factorId, fIdx) => (
                              <td key={`${row.ticker}-${factorId}-${fIdx}`} className="border border-gray-200 px-3 py-2">
                                {parseNumeric(row[factorId]) !== null ? formatNumber(parseNumeric(row[factorId]), 2) : 'N/A'}
                              </td>
                            ))}
                            <td className="border border-gray-200 px-3 py-2 text-emerald-700 font-medium">
                              {parseNumeric(row.weight_percent) !== null
                                ? formatPercent(parseNumeric(row.weight_percent))
                                : (parseNumeric(row.weight) !== null
                                  ? formatPercent(parseNumeric(row.weight) * 100)
                                  : 'N/A')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Selected Stocks ({selected.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {selected.length === 0 ? (
              <div className="text-sm text-gray-500">No selected stocks found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border-collapse border border-gray-200">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="border border-gray-200 px-3 py-2">Ticker</th>
                      <th className="border border-gray-200 px-3 py-2">Price</th>
                      {factorLabels.map((label, idx) => (
                        <th key={label + idx} className="border border-gray-200 px-3 py-2">
                          {label}
                        </th>
                      ))}
                      <th className="border border-gray-200 px-3 py-2">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.map((row, idx) => (
                      <tr key={row.ticker || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-200 px-3 py-2 font-medium">{row.ticker}</td>
                        <td className="border border-gray-200 px-3 py-2">
                          {parseNumeric(row.price) !== null ? `$${formatNumber(parseNumeric(row.price), 2)}` : 'N/A'}
                        </td>
                        {factorIds.map((factorId, fIdx) => (
                          <td key={`${row.ticker}-${factorId}-${fIdx}`} className="border border-gray-200 px-3 py-2">
                            {parseNumeric(row[factorId]) !== null ? formatNumber(parseNumeric(row[factorId]), 2) : 'N/A'}
                          </td>
                        ))}
                        <td className="border border-gray-200 px-3 py-2 text-emerald-700 font-medium">
                          {parseNumeric(row.weight_percent) !== null
                            ? formatPercent(parseNumeric(row.weight_percent))
                            : (parseNumeric(row.weight) !== null
                              ? formatPercent(parseNumeric(row.weight) * 100)
                              : 'N/A')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const SharedResultsPage = () => {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const loadShare = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchShareLink(shareId);
        if (isMounted) {
          setShare(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load shared results');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (shareId) {
      loadShare();
    }

    return () => {
      isMounted = false;
    };
  }, [shareId]);

  const isScreenResult = share?.resource_type === 'screen_result';
  const isSeasonalityResult = share?.resource_type === 'seasonality_result';
  const isChartResult = share?.resource_type === 'chart_result';
  const sharedRecord = share?.resource || null;

  const normalizedBacktest = useMemo(() => {
    if (!sharedRecord?.backtest_results) return null;
    const runCfg = sharedRecord?.backtest_config?.backtest || sharedRecord?.backtest_config?.run || {};
    return normalizeBacktestResult(sharedRecord.backtest_results, runCfg);
  }, [sharedRecord]);
  const isOptimizationResult = Boolean(normalizedBacktest?.optimization || normalizedBacktest?.is_optimization);

  const seasonalityPayload = sharedRecord?.seasonality_results || null;
  const seasonalityPoints = seasonalityPayload?.points || [];
  const seasonalityFocus = sharedRecord?.focus || seasonalityPayload?.focus || 'seasonality';
  const seasonalityLabel = seasonalityFocus.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const chartPayload = sharedRecord?.chart_results || null;
  const chartPoints = chartPayload?.points || [];
  const chartFormula = sharedRecord?.formula || chartPayload?.formula || 'Formula';
  const showChartPrice = chartPoints.length > 0 && chartFormula.trim().toLowerCase() !== 'price';

  const renderSharedSeasonality = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Seasonality Results</CardTitle>
      </CardHeader>
      <CardContent>
        {seasonalityPayload ? (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {sharedRecord?.symbol || seasonalityPayload?.symbol || 'Asset'} · {seasonalityLabel} ·
              {' '}
              {(sharedRecord?.start_date || seasonalityPayload?.start_date) ?? 'N/A'}
              {' '}
              to
              {' '}
              {(sharedRecord?.end_date || seasonalityPayload?.end_date) ?? 'N/A'}
              {' '}
              · {seasonalityPayload?.sample_count ?? 0} samples
              {seasonalityPayload?.dividends_included ? ' · Dividends included' : ''}
            </div>
            <div className="chart-container h-80 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={seasonalityPoints}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis tickFormatter={formatReturnPercent} />
                  <Tooltip formatter={(value) => formatReturnPercent(value)} />
                  <ReferenceLine y={0} stroke="#94a3b8" />
                  <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500">No seasonality results available.</div>
        )}
      </CardContent>
    </Card>
  );

  const renderSharedChart = () => (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Chart Results</CardTitle>
      </CardHeader>
      <CardContent>
        {chartPayload ? (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {sharedRecord?.symbol || chartPayload?.symbol || 'Asset'} · {chartFormula} ·
              {' '}
              {(sharedRecord?.start_date || chartPayload?.start_date) ?? 'N/A'}
              {' '}
              to
              {' '}
              {(sharedRecord?.end_date || chartPayload?.end_date) ?? 'N/A'}
              {' '}
              · {chartPayload?.total_points ?? chartPoints.length} points
            </div>
            <div className="chart-container w-full" style={{ height: 384 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartPoints}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" tickFormatter={(value) => formatNumber(value, 4)} />
                  {showChartPrice && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(value) => (Number.isFinite(value) ? `$${value.toFixed(0)}` : 'N/A')}
                    />
                  )}
                  <Tooltip
                    formatter={(value, name) => {
                      if (!Number.isFinite(value)) return ['N/A', name];
                      return name === 'Price' ? [formatPrice(value), name] : [formatNumber(value, 4), name];
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="Formula"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                    yAxisId="left"
                    isAnimationActive={false}
                  />
                  {showChartPrice && (
                    <Line
                      type="monotone"
                      dataKey="price"
                      name="Price"
                      stroke="#94a3b8"
                      strokeWidth={2}
                      dot={false}
                      yAxisId="right"
                      isAnimationActive={false}
                    />
                  )}
                  <Customized component={ChartWatermark} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500">No chart results available.</div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-wide text-gray-500">Shared result</div>
          <h1 className="text-3xl font-bold text-gray-900 mt-2">
            {sharedRecord?.name || (isScreenResult
              ? 'Shared Screen Results'
              : isSeasonalityResult
                ? 'Shared Seasonality Results'
                : isChartResult
                  ? 'Shared Chart Results'
                : (isOptimizationResult ? 'Shared Optimization Results' : 'Shared Backtest Results'))}
          </h1>
          <div className="text-sm text-gray-500 mt-1">
            {share?.created_at ? `Shared on ${new Date(share.created_at).toLocaleDateString()}` : ' '}
          </div>
        </div>

        <div className="mb-8 rounded-xl border border-emerald-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {isScreenResult
                  ? 'Want to do similar screens (and backtests) yourself?'
                  : isSeasonalityResult
                    ? 'Want to run seasonality analyses yourself?'
                    : isChartResult
                      ? 'Want to build formula charts yourself?'
                      : 'Want to do similar backtests (and screens) yourself?'}
              </div>
              <p className="text-sm text-gray-600">Sign up or log in for free now.</p>
            </div>
            <div className="flex gap-3">
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                <Link to="/signup">Sign up free</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/login">Log in</Link>
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-sm text-gray-500">Loading shared results...</div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && share && (
          <div className="space-y-8">
            {isScreenResult ? (
              <SharedScreenResults record={sharedRecord} />
            ) : isSeasonalityResult ? (
              renderSharedSeasonality()
            ) : isChartResult ? (
              renderSharedChart()
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {isOptimizationResult ? 'Optimization Results' : 'Backtest Results'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {normalizedBacktest ? (
                    isOptimizationResult
                      ? <OptimizationResultsPanel result={normalizedBacktest} />
                      : <EnhancedResultsPanel result={normalizedBacktest} isRunning={false} />
                  ) : (
                    <div className="text-sm text-gray-500">
                      {isOptimizationResult ? 'No optimization results available.' : 'No backtest results available.'}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedResultsPage;
